// apps/web/src/pages/spread/BatchSetCard.tsx
//
// Phase 9 Plan 11 Task 2 — single batch-set row.
// Phase 11.7 Plan 03 Task 1 — Surface-2 multi-batch-set affordances added:
//   - Surface 2a: a segmented On/Off Allow toggle that WRITES
//     `batchSets.{index}.allowSameAcrossBatches` (no Radix Switch — `switch.tsx`
//     is ABSENT; the segmented pill is forced AND is the reference-faithful
//     affordance). Accent-fill on the active segment mirrors Pill `selected`.
//   - Surface 2b: a Duplicate pill. When blocked (Allow off, or a constraint
//     verdict is violated) it renders the IMPORTED `DisabledOptionWithTooltip`
//     from the Wave-1 shared module `./constraint-pill` (aria-disabled, focusable,
//     tooltip) — NEVER a re-implemented or cross-imported copy. When enabled it
//     fires `onDuplicate` (BatchSetStrip owns `useFieldArray.append`).
//   - Surface 2c: a per-batch flavor picker scoped to `batchSets.{index}.selections`,
//     built from the SAME shared primitives Surface 1 uses (the shared `toggle`
//     for cardinality, `Pill` for the affordance, `resolveGroupLabel` for column
//     headers, the imported `DisabledOptionWithTooltip` for blocked options) —
//     NOT by importing Plan 02's `OptionSetSurface`. It writes the RHF `selections`
//     scope via per-path `useWatch` + setValue (a DIFFERENT state binding than
//     Surface 1's RecipeStateContext, so the per-batch picker needs NO
//     RecipeStateProvider scope — see SUMMARY).
//
// Renders inside a SortableItem (BatchSetStrip wraps each card in the
// SortableItem drag-handle composition). useTerm drives ALL copy (the
// pages/spread/** hardcoded-vocabulary grep gate). AUTHR-06 hygiene: per-path
// useWatch ONLY (name + allowSameAcrossBatches + selections). The strip-level
// useFieldArray provides remove() + the append-clone callback.

import { X } from "lucide-react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { ConstraintVerdict } from "@the-recipe-spread/shared";
import { substituteExplanation } from "@the-recipe-spread/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Pill } from "@/components/ui/pill";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTerm } from "@/lib/vocabulary/useTerm";
// Phase 11.7 Plan 03 — the Duplicate pill's disabled state IMPORTS the lifted
// DisabledOptionWithTooltip from the Wave-1 shared module (Plan 01). It is NOT
// re-implemented here and NOT cross-imported from RecipeOptionGroup.tsx (that
// would reach into Plan 02's same-wave file and re-introduce Pitfall 1 drift).
// firstViolatedFor is the v1 any-violated heuristic for the verdict-targeting
// path; imported from the same module.
import { DisabledOptionWithTooltip, firstViolatedFor } from "./constraint-pill";
// Surface 2c per-batch picker primitives — the SAME Wave-1 seams Surface 1 uses.
import { toggle } from "@/widgets/option-set/toggle";
import { resolveGroupLabel } from "@/widgets/summary-checklist/group-label";
import { BatchSetRecipePicker } from "./BatchSetRecipePicker";

export interface BatchSetCardProps {
  index: number;
  onRemove: () => void;
  // Surface 2b — the strip owns useFieldArray.append; the card calls back to
  // clone the active row when the Duplicate pill is enabled. Optional so the
  // Phase-9 callers that don't yet pass it degrade to a no-op clone.
  onDuplicate?: () => void;
  // Surface 2b — optional violated verdict for the v1 any-violated constraint
  // path. When present (a future composer pass threads it from
  // useSpreadConstraintEvaluation), the Duplicate pill is constraint-disabled
  // with the substituted explanation. Today the deterministic disable reason is
  // the Allow toggle being off; the verdict seam is preserved for when a
  // verdict source is wired (precise per-batch targeting DEFERRED — Phase 13+).
  violatedVerdicts?: ReadonlyArray<ConstraintVerdict> | undefined;
}

// Selection-state shape inside a batch set's `selections` record. Each entry is
// keyed by an option id; the value carries a `selected` flag (mirrors the
// option-set render-state {selected} convention). Unknown shapes degrade to
// not-selected so a malformed payload never throws at render.
function isSelectedEntry(value: unknown): boolean {
  if (typeof value === "object" && value !== null && "selected" in value) {
    return (value as { selected?: unknown }).selected === true;
  }
  return value === true;
}

export function BatchSetCard({
  index,
  onRemove,
  onDuplicate,
  violatedVerdicts,
}: BatchSetCardProps): React.ReactElement {
  const { control, setValue } = useFormContext();
  // Read all four vocabulary slots up-front for stable hook order (mirrors
  // useTermReader in RecipeOptionGroup.tsx:404-425). ALL copy routes through
  // these; `set` backs the substituteExplanation reader (constraint path).
  const container = useTerm("container");
  const subdivision = useTerm("subdivision");
  const item = useTerm("item");
  const set = useTerm("set");
  // Total reader closure for substituteExplanation — plain closure over the
  // four already-read slots (no hook calls inside, so it is stable + safe to
  // pass anywhere).
  const readVocab = (slot: "container" | "subdivision" | "set" | "item"): string => {
    switch (slot) {
      case "container":
        return container;
      case "subdivision":
        return subdivision;
      case "item":
        return item;
      case "set":
        return set;
    }
  };
  const [removeOpen, setRemoveOpen] = useState(false);

  // Per-path useWatch — narrow subscriptions (Pitfall 1 + 2 hygiene; AUTHR-06).
  const nameValue = useWatch({
    control,
    name: `batchSets.${index}.name`,
  });
  const allowSame = useWatch({
    control,
    name: `batchSets.${index}.allowSameAcrossBatches`,
  }) as boolean | undefined;
  const selections = useWatch({
    control,
    name: `batchSets.${index}.selections`,
  }) as Record<string, unknown> | undefined;

  const allowOn = allowSame === true;
  const labelForDialog =
    typeof nameValue === "string" && nameValue.length > 0
      ? nameValue
      : `${subdivision} ${index + 1}`;

  // The Allow toggle's accessible group label, vocab-substituted. Reused as the
  // Allow-off tooltip reason on the Duplicate pill.
  const allowLabel = `Allow same ${item} in multiple ${subdivision}s`;

  // Constraint-verdict targeting (v1 any-violated heuristic). The card's own
  // instance scope is the batch-set index; firstViolatedFor surfaces the first
  // violated verdict if a verdict source is threaded. Allow-off takes
  // precedence as the deterministic, always-available disable reason.
  const violated =
    violatedVerdicts !== undefined
      ? firstViolatedFor(`batchSets.${index}`, violatedVerdicts)
      : undefined;
  const isConstraintViolated = violated !== undefined && violated.status === "violated";

  // The Duplicate pill is blocked when Allow is off OR a constraint is violated.
  const duplicateBlocked = !allowOn || isConstraintViolated;

  function setAllow(next: boolean): void {
    setValue(`batchSets.${index}.allowSameAcrossBatches`, next, {
      shouldDirty: true,
    });
  }

  function handleConfirmRemove(): void {
    setRemoveOpen(false);
    onRemove();
  }

  function handleDuplicate(): void {
    if (duplicateBlocked) return;
    onDuplicate?.();
  }

  // Surface 2c — per-batch flavor selection over `selections`. The option ids
  // present in the batch's `selections` scope are the pickable options (this is
  // render wiring over the EXISTING shape — there is no resolved recipe-options
  // source in the card's form context; resolving the recipe would require a new
  // fetch/schema, out of scope here). Built from the SAME shared primitives as
  // Surface 1, scoped to the RHF `selections` path (NOT RecipeStateContext).
  const selectionOptionIds: string[] =
    selections !== undefined ? Object.keys(selections) : [];
  const selectedIds: string[] = selectionOptionIds.filter((id) =>
    isSelectedEntry(selections?.[id]),
  );
  // v1: cardinality is unbounded over the per-batch `selections` scope (no
  // recipe-config max available in this context); the shared toggle's guards
  // collapse to plain add/remove with min 0 / no max.
  const pickerBounds = { min: 0, max: undefined as number | undefined };
  const onTogglePick = (id: string): void => {
    const next = toggle(selectedIds, id, pickerBounds);
    const nextSet = new Set(next);
    const updated: Record<string, unknown> = { ...(selections ?? {}) };
    for (const optId of selectionOptionIds) {
      // Merge into the existing entry rather than replacing it, so any sibling
      // fields a richer/cloned entry carries (isSelectedEntry tolerates
      // `{ selected: true, ... }`; duplicateBatchSet deep-clones rows) survive
      // the toggle — preserving the round-trip-lossless storage contract
      // (11.7-REVIEW WR-04).
      const prev = (selections?.[optId] ?? {}) as Record<string, unknown>;
      updated[optId] = { ...prev, selected: nextSet.has(optId) };
    }
    setValue(`batchSets.${index}.selections`, updated, { shouldDirty: true });
  };

  return (
    <div className="rounded-md border border-input bg-card p-3 flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <FormField
            control={control}
            name={`batchSets.${index}.name`}
            render={({ field }) => (
              <FormItem data-field-path={`batchSets.${index}.name`}>
                <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {subdivision} name
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    aria-label={`${subdivision} name`}
                    placeholder={`${subdivision} ${index + 1}`}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <button
          type="button"
          aria-label={`Remove ${subdivision} ${labelForDialog}`}
          onClick={() => setRemoveOpen(true)}
          className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X size={14} aria-hidden />
        </button>
      </div>
      {/* Recipe picker — Plan 09-12 fills the Plan 09-11 slot. Uses shadcn
          Command (cmdk) via the BatchSetRecipePicker component. */}
      <BatchSetRecipePicker index={index} />

      {/* Surface 2a — segmented On/Off Allow toggle. role=radiogroup with two
          role=radio segments; the active segment carries the accent-fill via
          the Pill `selected` visual. Writes allowSameAcrossBatches. */}
      <div className="flex flex-col gap-1">
        <span
          id={`batchSets-${index}-allow-label`}
          className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
        >
          {allowLabel}
        </span>
        <div
          role="radiogroup"
          aria-labelledby={`batchSets-${index}-allow-label`}
          className="inline-flex w-fit gap-1 rounded-full border border-[var(--recipe-line)] p-0.5"
        >
          <Pill
            role="radio"
            selected={allowOn}
            aria-checked={allowOn}
            data-allow-on
            onClick={() => setAllow(true)}
          >
            On
          </Pill>
          <Pill
            role="radio"
            selected={!allowOn}
            aria-checked={!allowOn}
            data-allow-off
            onClick={() => setAllow(false)}
          >
            Off
          </Pill>
        </div>
      </div>

      {/* Surface 2b — Duplicate pill. Blocked → imported DisabledOptionWithTooltip
          (Allow-off generic reason OR substituted constraint explanation).
          Enabled → a focusable pill that fires onDuplicate (clone via the strip's
          useFieldArray.append). */}
      <div className="flex flex-col gap-1">
        {duplicateBlocked ? (
          isConstraintViolated && violated !== undefined ? (
            <DisabledOptionWithTooltip
              optionId={`batchSets-${index}-duplicate`}
              label="Duplicate"
              kindLabel={`${violated.kind.toUpperCase()} RULE`}
              explanation={substituteExplanation(violated.explanation, readVocab)}
            />
          ) : (
            // Allow-off reason — generic useTerm-substituted body (NOT a
            // constraint explanation). Reuses the same imported tooltip
            // component for a single implementation.
            <DisabledOptionWithTooltip
              optionId={`batchSets-${index}-duplicate`}
              label="Duplicate"
              kindLabel=""
              explanation={`Enable "${allowLabel}" first.`}
            />
          )
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleDuplicate}
                data-duplicate-enabled
                className="inline-flex w-fit items-center rounded-full border border-[var(--recipe-line)] bg-[var(--recipe-card)] px-3 py-1 text-sm text-[var(--recipe-ink)] hover:ring-2 hover:ring-[var(--recipe-accent-soft)] hover:border-[var(--recipe-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Duplicate
              </button>
            </TooltipTrigger>
            <TooltipContent className="recipe-tooltip max-w-xs border-[var(--recipe-line)] bg-[var(--recipe-card)] text-[var(--recipe-ink)]">
              <span className="text-sm leading-snug">
                {`Clone the active ${subdivision} into a new ${subdivision}.`}
              </span>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Surface 2c — per-batch flavor picker scoped to this batch's selections.
          Built from the shared primitives (Pill + toggle + resolveGroupLabel +
          DisabledOptionWithTooltip). Renders only when the batch has a selection
          scope (option ids present). */}
      {selectionOptionIds.length > 0 ? (
        <section
          role="group"
          aria-label={`${item} selection for ${labelForDialog}`}
          data-batch-picker={`batchSets.${index}.selections`}
          className="flex flex-col gap-2"
        >
          <div className="flex flex-wrap gap-2">
            {selectionOptionIds.map((id) => {
              // Cross-batch reuse blocked (Allow off + selected elsewhere)
              // surfaces via the SAME imported DisabledOptionWithTooltip
              // overlay; the v1 verdict path drives it when a verdict source is
              // threaded. Header resolution uses the shared resolveGroupLabel
              // (no optionCategories in this context → raw-id/empty fallback).
              const header = resolveGroupLabel(id, undefined);
              const optLabel = header !== "" ? header : id;
              return (
                <Pill
                  key={id}
                  selected={isSelectedEntry(selections?.[id])}
                  data-option-id={id}
                  onClick={() => onTogglePick(id)}
                >
                  {optLabel}
                </Pill>
              );
            })}
          </div>
        </section>
      ) : null}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {subdivision}?</DialogTitle>
            <DialogDescription>
              {labelForDialog} will be removed from this {container}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              autoFocus
              onClick={() => setRemoveOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleConfirmRemove}>
              Delete {subdivision}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
BatchSetCard.displayName = "BatchSetCard";
